import { useState, useRef, useEffect } from "react"
import { Input as NextUIInput } from "@nextui-org/input"
import cx from "classnames"

type HeadphoneBrand = {
  id: number
  name: string
}

type Props = {
  brands: HeadphoneBrand[]
  value: number | null
  onChange: (brandId: number | null) => void
  isInvalid?: boolean
}

// Generic brand autocomplete that can work with both number and string IDs
type GenericBrand = {
  id: number | string
  name: string
}

type GenericBrandAutocompleteProps<T extends number | string> = {
  brands: Array<{ id: T; name: string }>
  value: T | null
  onChange: (brandId: T | null) => void
  placeholder?: string
  isInvalid?: boolean
}

export const BrandAutocomplete = <T extends number | string>({
  brands,
  value,
  onChange,
  placeholder = "Search brand...",
  isInvalid = false,
}: GenericBrandAutocompleteProps<T>) => {
  const [searchValue, setSearchValue] = useState("")
  const [showSuggestions, setShowSuggestions] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Get the selected brand name
  const selectedBrand = brands.find((brand) => brand.id === value)

  // Filter brands based on search
  const filteredBrands = brands.filter((brand) =>
    brand.name.toLowerCase().includes(searchValue.toLowerCase())
  )

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setSearchValue(newValue)
    setShowSuggestions(true)
    
    // Clear selection immediately when input becomes empty
    if (newValue === "" && selectedBrand) {
      onChange(null)
    }
  }

  const handleBrandSelect = (brand: { id: T; name: string } | null) => {
    onChange(brand?.id || null)
    setSearchValue("")
    setShowSuggestions(false)
  }

  // Clear selection when input is cleared
  const handleInputBlur = () => {
    if (!searchValue && selectedBrand) {
      onChange(null)
    }
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <NextUIInput
        variant="bordered"
        value={searchValue || selectedBrand?.name || ""}
        onChange={handleInputChange}
        onFocus={() => {
          setShowSuggestions(true)
          if (selectedBrand) {
            setSearchValue("")
          }
        }}
        onBlur={handleInputBlur}
        classNames={{
          inputWrapper: [
            "px-4 h-7 min-h-7",
            "border rounded-plus !shadow-auto-save-dropdown",
            isInvalid
              ? "bg-brown border-red group-data-[focus=true]:border-red data-[hover=true]:border-red animate-pulse"
              : "bg-[#232528] border-[#383C4A] group-data-[focus=true]:border-[#626675] data-[hover=true]:border-[#626675] data-[hover=true]:bg-[#272A2F]",
          ],
          input: [
            cx(
              "text-xs font-bulo font-bold",
              isInvalid
                ? "text-dark-red placeholder:text-dark-red placeholder:text-opacity-50"
                : "text-ghost group-data-[has-value=true]:text-white placeholder:text-ghost placeholder:italic"
            ),
          ],
        }}
        placeholder={placeholder}
      />
      {showSuggestions && (searchValue || filteredBrands.length > 0) && (
        <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-plus border border-[#2E3037] bg-[#212121] p-0 shadow-outside text-ghost max-h-48 overflow-y-auto">
          <button
            className="w-full px-3 py-1.5 text-left font-bulo text-xs font-bold text-ghost hover:bg-mine-shaft hover:text-ghost rounded-plus"
            onClick={() => handleBrandSelect(null)}
          >
            Any Brand
          </button>
          {filteredBrands.map((brand) => (
            <button
              key={brand.id}
              className="w-full px-3 py-1.5 text-left font-bulo text-xs font-bold text-ghost hover:bg-mine-shaft hover:text-ghost rounded-plus"
              onClick={() => handleBrandSelect(brand)}
            >
              {brand.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const HeadphoneBrandAutocomplete = ({ brands, value, onChange, isInvalid }: Props) => {
  const [searchValue, setSearchValue] = useState("")
  const [showSuggestions, setShowSuggestions] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Get the selected brand name
  const selectedBrand = brands.find((brand) => brand.id === value)

  // Filter brands based on search
  const filteredBrands = brands.filter((brand) =>
    brand.name.toLowerCase().includes(searchValue.toLowerCase())
  )

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setSearchValue(newValue)
    setShowSuggestions(true)
    
    // Clear selection immediately when input becomes empty
    if (newValue === "" && selectedBrand) {
      onChange(null)
    }
  }

  const handleBrandSelect = (brand: HeadphoneBrand | null) => {
    onChange(brand?.id || null)
    setSearchValue("")
    setShowSuggestions(false)
  }

  // Clear selection when input is cleared
  const handleInputBlur = () => {
    if (!searchValue && selectedBrand) {
      onChange(null)
    }
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <NextUIInput
        variant="bordered"
        value={searchValue || selectedBrand?.name || ""}
        onChange={handleInputChange}
        onFocus={() => {
          setShowSuggestions(true)
          if (selectedBrand) {
            setSearchValue("")
          }
        }}
        onBlur={handleInputBlur}
        classNames={{
          inputWrapper: [
            "px-4 h-10",
            "border rounded-plus !shadow-inside-strong",
            isInvalid
              ? "bg-brown border-red group-data-[focus=true]:border-red data-[hover=true]:border-red animate-pulse"
              : "bg-cod-gray border-mine-shaft group-data-[focus=true]:border-silver data-[hover=true]:border-earth",
          ],
          input: [
            cx(
              "text-xs font-bulo font-bold text-ghost placeholder:italic",
              isInvalid
                ? "placeholder:text-dark-red placeholder:text-opacity-50"
                : "placeholder:text-earth"
            ),
          ],
        }}
        placeholder="Search brand..."
      />
      {showSuggestions && (searchValue || filteredBrands.length > 0) && (
        <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-plus border border-[#393939] bg-dark p-1.25 shadow-outside">
          <button
            className="w-full px-3 py-1.5 text-left font-bulo text-xs font-bold text-ghost hover:bg-mine-shaft hover:text-ghost"
            onClick={() => handleBrandSelect(null)}
          >
            None
          </button>
          {filteredBrands.map((brand) => (
            <button
              key={brand.id}
              className="w-full px-3 py-1.5 text-left font-bulo text-xs font-bold text-ghost hover:bg-mine-shaft hover:text-ghost"
              onClick={() => handleBrandSelect(brand)}
            >
              {brand.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default HeadphoneBrandAutocomplete
